import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useState } from 'react';
import Sentry from 'src/utils/sentry';
import { isITAREnvironment } from 'src/utils/validation';

const YBugContext = createContext(null);

export const useYBugApi = () => useContext(YBugContext);

export const YBugProvider = ({ yBugId, url, settings = {}, children }) => {
  const [yBugApi, setYBugApi] = useState(null);

  useEffect(() => {
    if (!yBugId) {
      return; // No YBug ID, do nothing
    }

    if (isITAREnvironment()) {
      Sentry.captureException(
        'An attempt to access YBug from the ITAR environment. Initialization has been prevented.'
      );
      return; // ITAR environment, prevent initialization
    }

    // Set global Ybug settings
    // eslint-disable-next-line camelcase
    window.ybug_settings = {
      ...settings,
      id: yBugId,
      onload: () => {
        setYBugApi(window.Ybug);
        if (settings.onload) {
          settings.onload();
        }
      },
    };

    // Create and append the Ybug script
    const script = document.createElement('script');
    script.defer = true;
    script.src = url || `https://widget.ybug.io/button/${yBugId}.js`;
    if (settings?.nonce) {
      script.nonce = settings.nonce; // Optional: Add nonce for CSP
    }
    document.head.append(script);

    // Cleanup function
    return () => {
      if (window.Ybug) {
        try {
          window.Ybug.destroy();
        } catch (e) {
          // Handle possible destroy errors gracefully
          Sentry.captureException(e);
        }
        delete window.Ybug;
      }
      script.remove();
      delete window.ybug_settings;
    };
  }, [yBugId]);

  return <YBugContext.Provider value={yBugApi}>{children}</YBugContext.Provider>;
};

YBugProvider.defaultProps = {
  settings: {},
  url: null,
};

YBugProvider.propTypes = {
  yBugId: PropTypes.string.isRequired,
  url: PropTypes.string,
  settings: PropTypes.shape({}),
  children: PropTypes.node.isRequired,
};
